import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Play, Archive, Edit3, Info } from "lucide-react";
import { PageHeader } from "../components/layout/PageHeader";
import { Button } from "../components/ui/button";
import { StatusPill } from "../components/saga/StatusPill";
import { SagaDefinitionEditor } from "../components/saga/SagaDefinitionEditor";
import { listSagaDefinitionVersions, activateSagaDefinition, archiveSagaDefinition, getStepCatalog } from "../services/saga-definition";
import type { SagaDefinitionView, StepCatalogEntry } from "../types/saga-definition";
import { RequireRole } from "../components/RequireRole";

export default function SagaDefinitionDetail() {
  const { name } = useParams<{ name: string }>();
  const [versions, setVersions] = useState<SagaDefinitionView[] | null>(null);
  const [catalog, setCatalog] = useState<StepCatalogEntry[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const reload = async () => {
    if (!name) return;
    try {
      const [vs, cat] = await Promise.all([listSagaDefinitionVersions(name), getStepCatalog()]);
      setVersions(vs);
      setCatalog(cat);
      // Seleciona active primeiro; senão a versão mais recente
      const active = vs.find((v) => v.active);
      setSelectedId(active?.id ?? vs[0]?.id ?? null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Falha ao carregar");
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);

  const onActivate = async (id: string) => {
    if (!confirm("Ativar esta versão? Outras versões active dessa saga serão desativadas.")) return;
    try {
      const updated = await activateSagaDefinition(id);
      toast.success(`v${updated.version} ativada`);
      await reload();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Falha ao ativar");
    }
  };

  const onArchive = async (id: string) => {
    if (!confirm("Arquivar esta versão? (não pode ser ativada novamente)")) return;
    try {
      await archiveSagaDefinition(id);
      toast.success("Arquivada");
      await reload();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Falha ao arquivar");
    }
  };

  const selected = versions?.find((v) => v.id === selectedId) ?? null;

  return (
    <>
      <PageHeader
        title={
          <span className="inline-flex items-center gap-3 font-mono-tight">
            {name}
            {selected?.active && <StatusPill status="OK" />}
          </span>
        }
        subtitle="Composição versionada — active alimenta novas execuções"
        actions={
          <div className="flex items-center gap-2">
            <Link to="/sagas/definitions">
              <Button size="sm" variant="outline"><ArrowLeft size={14} /> Definitions</Button>
            </Link>
            <RequireRole anyOf={["platform-master", "platform-admin"]}>
              <Link to={`/sagas/definitions/new?clone=${encodeURIComponent(name ?? "")}`}>
                <Button size="sm" variant="primary"><Plus size={14} /> Nova versão</Button>
              </Link>
            </RequireRole>
          </div>
        }
      />

      <div className="p-6 space-y-4">
        {err && <div className="p-3 rounded-md bg-danger-soft text-danger-soft-fg text-sm">{err}</div>}
        {!versions && !err && <p className="text-sm text-muted-foreground">Carregando…</p>}

        {versions && versions.length > 0 && (
          <>
            <section>
              <h2 className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-2">
                Versões ({versions.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {versions.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => setSelectedId(v.id)}
                    className={
                      "rounded-md border p-3 text-left transition-colors " +
                      (v.id === selectedId
                        ? "border-primary/40 bg-primary/5"
                        : "border-border bg-surface hover:border-primary/30")
                    }
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-mono-tight text-sm">v{v.version}</span>
                      {v.active && (
                        <span className="text-[10px] uppercase tracking-wider text-success font-medium">active</span>
                      )}
                      {v.archivedAt && (
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">archived</span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {v.steps.length} step{v.steps.length !== 1 ? "s" : ""} · {new Date(v.createdAt).toLocaleString("pt-BR")}
                    </p>
                  </button>
                ))}
              </div>
            </section>

            {selected && catalog && (
              <section>
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                    Versão {selected.version} {selected.active ? "(active)" : ""}
                  </h2>
                  <RequireRole anyOf={["platform-master", "platform-admin"]}>
                    <div className="flex items-center gap-2">
                      {!selected.active && !selected.archivedAt && (
                        <Button size="sm" variant="primary" onClick={() => onActivate(selected.id)}>
                          <Play size={12} /> Ativar
                        </Button>
                      )}
                      {!selected.archivedAt && (
                        <Button size="sm" variant="outline" onClick={() => onArchive(selected.id)}>
                          <Archive size={12} /> Arquivar
                        </Button>
                      )}
                    </div>
                  </RequireRole>
                </div>

                {/* SPEC-20260507-0136 (Fase 3) UX: dica clara de que esta página é read-only.
                    Edição = sempre criar nova versão (preserva snapshot pra sagas em flight). */}
                <RequireRole anyOf={["platform-master", "platform-admin"]}>
                  <div className="mb-3 rounded-md border border-primary/20 bg-primary/5 p-3 flex items-start gap-2 text-xs">
                    <Info size={14} className="text-primary shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium text-foreground">
                        Esta visualização é read-only
                      </p>
                      <p className="text-muted-foreground mt-0.5">
                        Sagas em flight usam o snapshot da versão active — alterações criam <strong>nova versão</strong>{" "}
                        (com mesmos steps, prontos pra reorder/edit). Steps são blocos pré-construídos do catálogo;{" "}
                        criar step novo (lógica nova) exige PR no orchestrator.
                      </p>
                    </div>
                    <Link to={`/sagas/definitions/new?clone=${encodeURIComponent(name ?? "")}`}>
                      <Button size="sm" variant="primary"><Edit3 size={12} /> Editar (nova versão)</Button>
                    </Link>
                  </div>
                </RequireRole>

                <SagaDefinitionEditor
                  catalog={catalog}
                  initialName={selected.name}
                  initialDescription={selected.description ?? ""}
                  initialSteps={selected.steps}
                  onSave={async () => {
                    /* read-only — botão não aparece */
                  }}
                  readOnly
                />
              </section>
            )}
          </>
        )}
      </div>
    </>
  );
}
